import { useEffect, useMemo, useRef, useState } from "react";
import "./Notifications.css";
import "./CampaignOptionCard.css";
import TextInput from "../../component/textInput/TextInput";
import {
  PrimaryButton,
  SecondaryButton,
} from "../../component/buttonComponent/ButtonComponent";
import CustomDatePicker from "../../component/datePicker/DatePicker";
import CustomTimePicker from "../../component/timePicker/TimePicker";
import Cookies from "js-cookie";
import { formatInTimeZone } from "date-fns-tz";
import {
  getUTCOffset,
  isScheduledDateTimeInFuture,
} from "../../config/utilities/fDate";
import { useTimezone } from "../../config/utilities/useTimezone";
import { useAuth } from "../../config/utilities/useAuth";
import { RequiredLabel } from "../../component/requiredLabel/RequiredLabel";
import { APIGetSchedules } from "../../config/utilities/APIs/schedulingApi";
import CampaignMessageEditor from "./CampaignMessageEditor";
import { ENABLE_EXTENDED_CAMPAIGN_AUDIENCES } from "../../config/featureFlags";
import {
  CAMPAIGN_DELIVERY_OPTIONS,
  getVisibleAudienceOptions,
} from "./campaignFormConfig.js";
import {
  clearAudienceFieldsForType,
  buildFamilyLabelsFromDetails,
  buildScheduleLabelsFromDetails,
  buildStopLabelsFromDetails,
  formatScheduleOptionLabel,
  mapScheduleDetailsToAudienceOptions,
  getAudienceSelectionError,
  getCampaignFormSteps,
  getDefaultAudienceFields,
  isAudienceSelectionValid,
  mapScheduleToAudienceOption,
  stripHtmlToPlainText,
  validateCampaignStepByKey,
} from "./campaignFormHelpers.js";
import { parseCampaignAudienceFromApi } from "../../config/utilities/campaignAudiencePayload.js";
import CampaignOptionCard from "./CampaignOptionCard";
import CampaignAudiencePanel from "./CampaignAudiencePanel";
import CampaignFormSummary from "./CampaignFormSummary";

export const CampaignForm = ({
  mode,
  existingData,
  onSubmit,
  handleReset,
  loading,
  input,
  setInput,
  error,
  setError,
}) => {
  const { privilege, tripTypes } = useAuth();
  const { timezone } = useTimezone();
  const institutionTz =
    timezone || Cookies.get("timezone") || "Asia/Kuala_Lumpur";
  const institutionId = privilege?.institutionId;

  const institutionTimezoneHelp = useMemo(() => {
    const tz = institutionTz;
    const offset = getUTCOffset(tz);
    const label = tz.replace(/_/g, " ");
    return `Times use the ${label} (${offset}) timezone.`;
  }, [institutionTz]);

  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
  const [initialInput, setInitialInput] = useState(null);
  const [keyUp, setKeyUp] = useState("", false);
  const [scheduleLookup, setScheduleLookup] = useState(() => ({
    scheduleLookup: formatInTimeZone(new Date(), institutionTz, "yyyy-MM-dd"),
  }));
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [scheduleData, setScheduleData] = useState([]);
  const [scheduleLabels, setScheduleLabels] = useState({});
  const [familyLabels, setFamilyLabels] = useState({});
  const [stopLabels, setStopLabels] = useState({});
  const [isMobileViewport, setIsMobileViewport] = useState(
    typeof window !== "undefined" ? window.innerWidth <= 768 : false,
  );
  const [currentStep, setCurrentStep] = useState(0);
  const [stepError, setStepError] = useState("");
  const [showAudienceErrors, setShowAudienceErrors] = useState(false);

  const skipScheduleLookupEffect = useRef(false);
  const formBodyRef = useRef(null);
  const formSteps = useMemo(() => getCampaignFormSteps(mode), [mode]);
  const lastStepIndex = formSteps.length - 1;
  const isLastStep = currentStep === lastStepIndex;
  const activeStepKey = formSteps[currentStep]?.key;
  const isNextStepDisabled = useMemo(() => {
    if (mode === "view") return false;
    if (!activeStepKey) return true;
    return !validateCampaignStepByKey(activeStepKey, input, institutionTz).valid;
  }, [mode, activeStepKey, input, institutionTz]);
  const audienceOptions = useMemo(
    () => getVisibleAudienceOptions(ENABLE_EXTENDED_CAMPAIGN_AUDIENCES),
    [],
  );

  useEffect(() => {
    const handleResize = () =>
      setIsMobileViewport(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    setCurrentStep(0);
    setStepError("");
    setShowAudienceErrors(false);
  }, [mode, existingData]);

  useEffect(() => {
    formBodyRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  }, [currentStep]);

  const handleNextStep = () => {
    if (currentStep >= lastStepIndex) return;

    if (mode !== "view") {
      const { valid, message } = validateCampaignStepByKey(
        activeStepKey,
        input,
        institutionTz,
      );
      if (!valid) {
        setStepError(message);
        if (activeStepKey === "audience") {
          setShowAudienceErrors(true);
        }
        return;
      }
    }

    setStepError("");
    if (activeStepKey === "audience") {
      setShowAudienceErrors(false);
    }
    setCurrentStep((step) => Math.min(step + 1, lastStepIndex));
  };

  const handleBackStep = () => {
    setStepError("");
    setShowAudienceErrors(false);
    setCurrentStep((step) => Math.max(step - 1, 0));
  };

  const handleStepSelect = (stepIndex) => {
    if (mode === "view") {
      setStepError("");
      setCurrentStep(stepIndex);
      return;
    }

    if (stepIndex <= currentStep) {
      setStepError("");
      setCurrentStep(stepIndex);
      return;
    }

    for (let index = currentStep; index < stepIndex; index += 1) {
      const stepKey = formSteps[index]?.key;
      const { valid, message } = validateCampaignStepByKey(
        stepKey,
        input,
        institutionTz,
      );
      if (!valid) {
        setCurrentStep(index);
        setStepError(message);
        if (stepKey === "audience") {
          setShowAudienceErrors(true);
        }
        return;
      }
    }

    setStepError("");
    setCurrentStep(stepIndex);
  };

  const isScheduleDisabled =
    !input.alertType || ["onDemand", "sendNow"].includes(input.alertType);

  const fullWidth = "100%";
  const textFieldWidth = isMobileViewport ? fullWidth : "100%";
  const datetimeWidth = isMobileViewport ? fullWidth : "14rem";

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === "targetAudience") {
      setInput((prev) => ({
        ...prev,
        targetAudience: value,
        ...clearAudienceFieldsForType(value),
      }));
      return;
    }

    const updatedInput = { ...input, [name]: value };

    if (name === "alertType" && ["onDemand", "sendNow"].includes(value)) {
      updatedInput.scheduledDate = "";
      updatedInput.scheduledTime = "";
    } else if (name === "alertType") {
      const utc = new Date(Date.now() + 15 * 60 * 1000);
      updatedInput.scheduledDate = formatInTimeZone(
        utc,
        institutionTz,
        "yyyy-MM-dd",
      );
      updatedInput.scheduledTime = formatInTimeZone(utc, institutionTz, "HH:mm");
    }

    setInput(updatedInput);
    setStepError("");
  };

  const handleDeliverySelect = (value) => {
    setStepError("");
    handleInputChange({ target: { name: "alertType", value } });
  };

  const handleAudienceSelect = (value) => {
    setStepError("");
    setShowAudienceErrors(false);
    handleInputChange({ target: { name: "targetAudience", value } });
  };

  const isCampaignFormValid = () => {
    const isScheduleValid =
      mode !== "create" ||
      isScheduleDisabled ||
      isScheduledDateTimeInFuture(
        input.scheduledDate,
        input.scheduledTime,
        institutionTz,
      );

    const audienceValid = isAudienceSelectionValid(input);

    const newErrors = {
      name: input.name !== "" ? input.name.length > 100 : false,
      description:
        input.description !== "" ? input.description.length > 300 : false,
      message:
        input.message !== ""
          ? stripHtmlToPlainText(input.message).length > 1200
          : false,
      scheduledDateTime: !isScheduleValid,
      targetAudience: input.targetAudience?.trim() === "",
      scheduleAudience: audienceValid
        ? ""
        : getAudienceSelectionError(input),
    };

    const isValid =
      input.name.trim() !== "" &&
      input.description.trim() !== "" &&
      input.alertType.trim() !== "" &&
      isScheduleValid &&
      input.message.trim() !== "" &&
      audienceValid &&
      !newErrors.name &&
      !newErrors.description &&
      !newErrors.message;

    const isEdited =
      initialInput && JSON.stringify(initialInput) !== JSON.stringify(input);

    return isValid && isEdited;
  };

  useEffect(() => {
    const inFuture =
      input.scheduledDate &&
      input.scheduledTime &&
      isScheduledDateTimeInFuture(
        input.scheduledDate,
        input.scheduledTime,
        institutionTz,
      );

    const audienceError = showAudienceErrors
      ? getAudienceSelectionError(input)
      : "";

    setError((prev) => ({
      ...prev,
      name: input.name.length > 100,
      description: input.description.length > 300,
      message: stripHtmlToPlainText(input.message).length > 1200,
      scheduledDateTime:
        mode === "create" && !isScheduleDisabled && !inFuture,
      targetAudience: input.targetAudience?.trim() === "",
      scheduleAudience: audienceError,
      scheduleIds:
        showAudienceErrors &&
        input.targetAudience === "schedule" &&
        (!Array.isArray(input.scheduleIds) || input.scheduleIds.length === 0),
    }));
  }, [input, isScheduleDisabled, institutionTz, mode, setError, showAudienceErrors]);

  useEffect(() => {
    setIsSubmitDisabled(!isCampaignFormValid());
  }, [input, initialInput, isScheduleDisabled, institutionTz, mode]);

  useEffect(() => {
    if ((mode === "edit" || mode === "view") && existingData) {
      const audienceFields = parseCampaignAudienceFromApi(existingData);
      const merged = {
        ...getDefaultAudienceFields(),
        ...existingData,
        ...audienceFields,
      };
      setInitialInput(merged);

      setInput((prev) => ({
        ...prev,
        ...merged,
        name: existingData.name || prev.name || "",
        description: existingData.description || prev.description || "",
        message: existingData.message || prev.message || "",
        alertType: existingData.alertType ?? prev.alertType,
        scheduledDate: existingData.scheduledDate || prev.scheduledDate || "",
        scheduledTime: existingData.scheduledTime || prev.scheduledTime || "",
      }));

      if (audienceFields.targetAudience === "schedule") {
        skipScheduleLookupEffect.current = true;

        const labelsFromDetails = buildScheduleLabelsFromDetails(
          existingData.scheduleDetails,
        );
        if (Object.keys(labelsFromDetails).length > 0) {
          setScheduleLabels((prev) => ({ ...prev, ...labelsFromDetails }));
        }

        const optionsFromDetails = mapScheduleDetailsToAudienceOptions(
          existingData.scheduleDetails,
        );
        if (optionsFromDetails.length > 0) {
          setScheduleData(optionsFromDetails);
        }

        (async () => {
          setScheduleLoading(true);
          try {
            const selectedScheduleIds = audienceFields.scheduleIds;
            const selectedScheduleResults = await Promise.all(
              selectedScheduleIds.map((scheduleId) =>
                APIGetSchedules({ scheduleId }),
              ),
            );
            const selectedSchedules = selectedScheduleResults
              .map((result) => result?.data?.data?.schedules?.[0])
              .filter(Boolean);
            const selectedScheduleOptions = selectedSchedules.map(
              mapScheduleToAudienceOption,
            );
            setScheduleLabels((prev) => {
              const updates = { ...prev };
              selectedSchedules.forEach((schedule) => {
                updates[schedule._id] = formatScheduleOptionLabel(schedule);
              });
              return updates;
            });
            const firstSchedule = selectedSchedules[0];

            if (firstSchedule) {
              skipScheduleLookupEffect.current = true;

              setScheduleLookup({
                scheduleLookup: firstSchedule.scheduleDate,
              });

              const resultForDay = await APIGetSchedules({
                visibleRange: {
                  startDate: firstSchedule.scheduleDate,
                  endDate: firstSchedule.scheduleDate,
                },
                institutionId,
              });

              if (resultForDay?.data?.data?.schedules) {
                const formattedForDay = resultForDay.data.data.schedules.map(
                  mapScheduleToAudienceOption,
                );
                const mergedOptions = [
                  ...selectedScheduleOptions,
                  ...formattedForDay,
                ].filter(
                  (option, index, arr) =>
                    arr.findIndex((o) => o.value === option.value) === index,
                );

                setScheduleData(mergedOptions);
              }
            }
          } catch (err) {
            console.error("Error fetching schedule by ID:", err);
          } finally {
            setScheduleLoading(false);
          }
        })();
      }

      if (audienceFields.targetAudience === "family") {
        const labelsFromDetails = buildFamilyLabelsFromDetails(
          existingData.familyDetails,
        );
        if (Object.keys(labelsFromDetails).length > 0) {
          setFamilyLabels((prev) => ({ ...prev, ...labelsFromDetails }));
        }
      }

      if (audienceFields.targetAudience === "stop") {
        const labelsFromDetails = buildStopLabelsFromDetails(
          existingData.stopDetails,
        );
        if (Object.keys(labelsFromDetails).length > 0) {
          setStopLabels((prev) => ({ ...prev, ...labelsFromDetails }));
        }
      }
    } else {
      setInitialInput({
        ...input,
        ...getDefaultAudienceFields(),
      });
    }
  }, [mode, existingData]);

  useEffect(() => {
    if (mode === "view") return;
    if (input.targetAudience !== "schedule") return;

    const skipClear = skipScheduleLookupEffect.current;
    if (skipClear) {
      skipScheduleLookupEffect.current = false;
    }

    const fetchSchedules = async () => {
      const today = scheduleLookup.scheduleLookup;

      if (!skipClear) {
        setInput((prev) => ({ ...prev, scheduleId: "", scheduleIds: [] }));
      }
      setScheduleLoading(true);

      try {
        const result = await APIGetSchedules({
          visibleRange: { startDate: today, endDate: today },
          institutionId,
        });

        if (result?.data?.data?.schedules) {
          setScheduleData(
            result.data.data.schedules.map(mapScheduleToAudienceOption),
          );
        } else {
          setScheduleData([]);
        }
      } finally {
        setScheduleLoading(false);
      }
    };

    fetchSchedules();
  }, [scheduleLookup, mode, input.targetAudience, institutionId, setInput]);

  const renderSection = (title, description, children) => (
    <section className="campaign-form-section">
      <div className="campaign-form-section__header">
        <h2 className="campaign-form-section__title">{title}</h2>
        {description ? (
          <p className="campaign-form-section__description">{description}</p>
        ) : null}
      </div>
      <div className="campaign-form-section__body">{children}</div>
    </section>
  );

  const renderStepContent = (stepKey) => {
    switch (stepKey) {
      case "details":
        return (
          <>
            <div className="campaign-form-field">
              <RequiredLabel>Campaign name</RequiredLabel>
              <TextInput
                placeholder={
                  mode === "view" ? input.name : "Enter campaign name"
                }
                setInput={setInput}
                input={mode === "view" ? "" : input}
                name="name"
                disable={mode === "view"}
                type="text"
                error={error.name}
                setError={setError}
                errorMessage="Campaign name should not exceed 100 characters"
                keyUp={keyUp}
                setKeyUp={setKeyUp}
                onChangeFunction={handleInputChange}
                width={textFieldWidth}
                showCharCount
                maxLength={100}
              />
            </div>
            <div className="campaign-form-field">
              <RequiredLabel>Description</RequiredLabel>
              <TextInput
                placeholder={
                  mode === "view"
                    ? input.description
                    : "Example: Inform parents about a class cancellation."
                }
                type="textarea"
                input={mode === "view" ? "" : input}
                setInput={setInput}
                name="description"
                width={textFieldWidth}
                onChangeFunction={handleInputChange}
                setError={setError}
                error={error.description}
                errorMessage="Description should not exceed 300 characters"
                keyUp={keyUp}
                setKeyUp={setKeyUp}
                disable={mode === "view"}
                showCharCount
                maxLength={300}
              />
            </div>
          </>
        );
      case "delivery":
        return (
          <>
            <div
              className="campaign-option-card-grid campaign-option-card-grid--delivery"
              role="radiogroup"
              aria-label="Delivery timing"
            >
              {CAMPAIGN_DELIVERY_OPTIONS.map((option) => (
                <CampaignOptionCard
                  key={option.value}
                  name="alertType"
                  value={option.value}
                  title={option.title}
                  description={option.description}
                  selected={input.alertType === option.value}
                  disabled={mode !== "create"}
                  onSelect={handleDeliverySelect}
                />
              ))}
            </div>
            {input.alertType === "scheduled" ? (
              <div className="campaign-form-scheduled-fields">
                <p className="campaign-form-timezone-hint">
                  {institutionTimezoneHelp}
                </p>
                <div className="campaign-form-datetime-row">
                  <CustomDatePicker
                    disable={isScheduleDisabled || mode !== "create"}
                    label="Scheduled date"
                    placeholder="Select a date"
                    name="scheduledDate"
                    error={mode === "view" ? null : error.scheduledDateTime}
                    setError={setError}
                    input={input}
                    setInput={setInput}
                    errorMessage=""
                    width={datetimeWidth}
                    disablePast={true}
                  />
                  <CustomTimePicker
                    label="Scheduled time"
                    value={input.scheduledTime}
                    onChange={(val, name) =>
                      setInput((prev) => ({
                        ...prev,
                        [name]: val || "",
                      }))
                    }
                    name="scheduledTime"
                    error={mode === "view" ? null : error.scheduledDateTime}
                    errorMessage=""
                    setError={setError}
                    placeholder="Select a time"
                    disable={isScheduleDisabled || mode !== "create"}
                    width={datetimeWidth}
                    disablePast={true}
                    selectedDay={input.scheduledDate}
                  />
                </div>
                {mode === "create" && error.scheduledDateTime ? (
                  <p className="campaign-form-error visible">
                    Scheduled time must be in the future
                  </p>
                ) : null}
              </div>
            ) : null}
          </>
        );
      case "audience":
        return (
          <>
            <div
              className="campaign-option-card-grid"
              role="radiogroup"
              aria-label="Target audience"
            >
              {audienceOptions.map((option) => (
                <CampaignOptionCard
                  key={option.value}
                  name="targetAudience"
                  value={option.value}
                  title={option.title}
                  description={option.description}
                  selected={input.targetAudience === option.value}
                  disabled={mode === "view"}
                  onSelect={handleAudienceSelect}
                />
              ))}
            </div>

            {input.targetAudience ? (
              <CampaignAudiencePanel
                targetAudience={input.targetAudience}
                input={input}
                setInput={setInput}
                mode={mode}
                error={error}
                scheduleLookup={scheduleLookup}
                setScheduleLookup={setScheduleLookup}
                scheduleData={scheduleData}
                scheduleLoading={scheduleLoading}
                tripTypes={tripTypes}
                institutionId={institutionId}
                datetimeWidth={datetimeWidth}
                scheduleLabels={scheduleLabels}
                setScheduleLabels={setScheduleLabels}
                familyLabels={familyLabels}
                setFamilyLabels={setFamilyLabels}
                stopLabels={stopLabels}
                setStopLabels={setStopLabels}
              />
            ) : null}
          </>
        );
      case "message": {
        const isMessageStepActive = activeStepKey === "message";
        const messageFieldError =
          error.message || (isMessageStepActive && Boolean(stepError));
        const messageErrorText = messageFieldError
          ? isMessageStepActive && stepError
            ? stepError
            : !stripHtmlToPlainText(input.message)
              ? "Enter a message for parents."
              : "Message should not exceed 1200 characters."
          : "";

        return (
          <CampaignMessageEditor
            value={input.message}
            onChange={(value) => {
              setStepError("");
              setInput((prev) => ({ ...prev, message: value }));
            }}
            placeholder="Dear parents, …"
            readOnly={mode === "view"}
            error={messageFieldError}
            errorMessage={messageErrorText}
          />
        );
      }
      default:
        return null;
    }
  };

  const activeStep = formSteps[currentStep];

  return (
    <div className="campaign-form-layout">
      <nav
        className="campaign-form-stepper"
        aria-label="Campaign form steps"
      >
        {formSteps.map((step, index) => {
          const isActive = index === currentStep;
          const isComplete = index < currentStep;
          const canSelect =
            mode === "view" || index <= currentStep || isComplete;

          return (
            <button
              key={step.key}
              type="button"
              className={`campaign-form-stepper__step${
                isActive ? " campaign-form-stepper__step--active" : ""
              }${isComplete ? " campaign-form-stepper__step--complete" : ""}`}
              onClick={() => canSelect && handleStepSelect(index)}
              disabled={!canSelect}
              aria-current={isActive ? "step" : undefined}
            >
              <span className="campaign-form-stepper__index">
                {isComplete ? "✓" : index + 1}
              </span>
              <span className="campaign-form-stepper__label">
                {step.title}
              </span>
            </button>
          );
        })}
      </nav>

      <div className="campaign-form-content">
        <div className="campaign-form-body" ref={formBodyRef}>
          {activeStep
            ? renderSection(
                activeStep.title,
                activeStep.description,
                renderStepContent(activeStep.key),
              )
            : null}

          {stepError ? (
            <p className="campaign-form-error visible campaign-form-step-error">
              {stepError}
            </p>
          ) : null}
        </div>

        <CampaignFormSummary
          input={input}
          institutionTz={institutionTz}
          scheduleOptions={scheduleData}
          tripTypes={tripTypes}
          familyLabels={familyLabels}
          stopLabels={stopLabels}
          scheduleLabels={scheduleLabels}
          formSteps={formSteps}
          currentStep={currentStep}
        />
      </div>

      <div className="campaign-form-actions">
        <div className="campaign-form-actions__inner">
          {currentStep > 0 ? (
            <SecondaryButton
              width="6rem"
              label="Back"
              imageIcon={false}
              onClickFunction={handleBackStep}
            />
          ) : null}
          <SecondaryButton
            width="6rem"
            label="Cancel"
            imageIcon={false}
            onClickFunction={handleReset}
          />
          {!isLastStep ? (
            <PrimaryButton
              width="6rem"
              label="Next"
              disable={isNextStepDisabled}
              onClickFunction={handleNextStep}
            />
          ) : mode !== "view" ? (
            <PrimaryButton
              width="6rem"
              disable={isSubmitDisabled}
              label={mode === "create" ? "Create" : "Save"}
              onClickFunction={onSubmit}
              loading={loading}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
};
